import {
  type CreateDiscountCouponRequest,
  type CurrencyOption,
  CurrencyOptionListSchema,
  type DiscountCouponDetail,
  DiscountCouponDetailSchema,
  type DiscountCouponListQuery,
  type DiscountCouponListResponse,
  DiscountCouponListResponseSchema,
  type UpdateDiscountCouponRequest,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/**
 * Discount Coupon master API + currency options lookup. Every response is
 * validated with the shared Zod schema so a server change can't sneak past
 * the UI types.
 */

export const listDiscountCoupons = async (
  query: DiscountCouponListQuery,
): Promise<DiscountCouponListResponse> => {
  const { data } = await apiClient.get<unknown>('/discount-coupons', { params: query });
  return DiscountCouponListResponseSchema.parse(data);
};

export const getDiscountCoupon = async (id: string): Promise<DiscountCouponDetail> => {
  const { data } = await apiClient.get<unknown>(`/discount-coupons/${id}`);
  return DiscountCouponDetailSchema.parse(data);
};

export const createDiscountCoupon = async (
  payload: CreateDiscountCouponRequest,
): Promise<DiscountCouponDetail> => {
  const { data } = await apiClient.post<unknown>('/discount-coupons', payload);
  return DiscountCouponDetailSchema.parse(data);
};

export const updateDiscountCoupon = async (
  id: string,
  payload: UpdateDiscountCouponRequest,
): Promise<DiscountCouponDetail> => {
  const { data } = await apiClient.patch<unknown>(`/discount-coupons/${id}`, payload);
  return DiscountCouponDetailSchema.parse(data);
};

export const deleteDiscountCoupon = async (id: string): Promise<void> => {
  await apiClient.delete(`/discount-coupons/${id}`);
};

export const bulkDeleteDiscountCoupons = async (
  ids: string[],
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/discount-coupons/bulk-delete', { ids });
  return data;
};

export const bulkSetDiscountCouponsStatus = async (
  ids: string[],
  isActive: boolean,
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/discount-coupons/bulk-set-status', { ids, isActive });
  return data;
};

// ----- Currency options (slim dropdown for currencyId picker) -----

export const listCurrencyOptions = async (): Promise<CurrencyOption[]> => {
  const { data } = await apiClient.get<unknown>('/currencies/options');
  return CurrencyOptionListSchema.parse(data);
};
